// 전자결재 API 훅
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { SWRConfiguration } from 'swr';
import {
  Approval,
  ApprovalList,
  ApprovalParams,
  ApprovalType,
  FavoriteApprovalFilter,
  FavoriteFilter,
  FavoriteFilterData,
  RecommendFilterResponse,
  TotalApprovalParams,
} from '@/types/api';
import { LegacyApproval, Meta } from '@/types/legacyApi';
import { ApprovalAuthorStatusBadge } from '@/types/approval';
import {
  APPROVAL_FAVORITE_FILTER,
  APPROVAL_RECENT_SEARCH_KEYWORD,
  APPROVAL_RECOMMEND_FILTER_URL,
  APPROVAL_SEARCH_URL,
  APPROVAL_TYPE_ALL_URL,
  APPROVAL_TYPE_V2_LIST_URL,
  COMBINED_DEPARTMENT_URL,
  getApprovalBookmarkUrl,
  LEGACY_APPROVAL_AUTHOR_STATUS_BADGE_URL,
  LEGACY_APPROVAL_ME,
  LEGACY_APPROVAL_RECENT_READ,
} from '@/constant/approval';
import {
  deleteDataFetcher,
  getNestedDataFetcher,
  getNestedMetaDataFetcher,
  postDataFetcher,
  putDataFetcher,
} from '@/utils/apis';
import { fetchApi } from '@/utils/api/customAxios';
import {
  toLegacyApprovalItem,
  toTagByCode,
  V2SearchHit,
} from '@/utils/approval/approvalSearchV2';
import { Lang, normalizeLang, pickI18nText } from '@/utils/approval/i18nText';

// --- 조회 ---

// 작성 화면 양식 선택 목록 — V2 네이티브 응답. name/tag 는 다국어 객체.
type ApprovalTypeV2ListItem = {
  code: number;
  name: Partial<Record<Lang, string | null>> | string | null;
  tag: Partial<Record<Lang, string | null>> | string | null;
  sortNumber: number | null;
};

// 작성 화면 양식 목록. BE 가 작성자 소속 법인(ACL) 기준으로 필터한 목록을 내려준다(빈 acl=전체 노출, PORTAL-11920).
// V2 응답(name/tag 다국어 객체)을 기존 ApprovalType shape 로 매핑해 소비처는 무변경 유지한다.
export const useGetApprovalType = () => {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  const swr = useSWR<ApprovalTypeV2ListItem[]>(
    APPROVAL_TYPE_V2_LIST_URL,
    getNestedDataFetcher,
  );

  const data = useMemo<ApprovalType[] | undefined>(
    () =>
      swr.data?.map((item) => ({
        type: item.code, // v1 `type` = v2 `code` (동일 양식코드)
        name: pickI18nText(item.name, lang),
        tag: pickI18nText(item.tag, lang),
        sortNumber: item.sortNumber,
      })),
    [swr.data, lang],
  );

  return { ...swr, data };
};

export const useGetApprovalAllType = () =>
  useSWR<ApprovalType[]>(APPROVAL_TYPE_ALL_URL, getNestedDataFetcher);

export const useGetMyApprovalData = (
  obj: ApprovalParams,
  config?: SWRConfiguration,
) => {
  const newEntries = Object.entries(obj).map(([k, v]) => [
    k,
    typeof v === 'number' ? v.toString() : v,
  ]);
  const newObject = Object.fromEntries(newEntries) as Record<string, string>;

  const queryParam = new URLSearchParams(newObject);

  return useSWR<{ meta: Meta; data: Approval[] }>(
    `${LEGACY_APPROVAL_ME}?${queryParam}`,
    getNestedMetaDataFetcher,
    config,
  );
};

export const useRecentReadApproval = () =>
  useSWR<Approval[]>(LEGACY_APPROVAL_RECENT_READ, getNestedDataFetcher);

// 작성자 본인 문서의 완료/반려 카운트 (SNB '완료/반려' 뱃지). 자가결재 문서는 서버에서 제외됨.
export const useAuthorStatusBadge = () =>
  useSWR<ApprovalAuthorStatusBadge>(
    LEGACY_APPROVAL_AUTHOR_STATUS_BADGE_URL,
    getNestedDataFetcher,
  );

/** 결재 목록 검색 URL. 조립을 한 곳에 모아 두어 호출부끼리 조건이 어긋나지 않게 한다. */
export const buildApprovalSearchUrl = (obj: TotalApprovalParams) => {
  const filteredEntries = Object.entries(obj).filter(
    ([_, v]) =>
      v !== undefined && v !== '' && (Array.isArray(v) ? v.length > 0 : true),
  );

  const newEntries = filteredEntries.map(([k, v]) => [
    k,
    typeof v === 'number' ? v.toString() : v,
  ]);
  const newObject = Object.fromEntries(newEntries) as Record<string, string>;

  const queryParam = new URLSearchParams(newObject);

  return `${APPROVAL_SEARCH_URL}?${queryParam}`;
};

let typeTagsRequest: Promise<Map<number, string>> | null = null;

/** 양식 태그 — V2 검색 응답에 없어 양식 목록에서 채움. 세션당 한 번 조회 */
const loadTypeTags = () => {
  typeTagsRequest ??= getNestedDataFetcher<ApprovalType[]>(
    APPROVAL_TYPE_ALL_URL,
  )
    .then(toTagByCode)
    .catch(() => {
      typeTagsRequest = null;
      return new Map<number, string>();
    });
  return typeTagsRequest;
};

const toLegacyApprovalList = async <T>(
  items: T[] | undefined,
): Promise<T[]> => {
  if (!items?.length || 'docId' in (items[0] as object)) return items ?? [];
  const tags = await loadTypeTags();
  return items.map(
    (item) => toLegacyApprovalItem(item as unknown as V2SearchHit, tags) as T,
  );
};

/** 결재 목록 검색 fetcher({data, meta}) — V2 응답은 기존 목록 형태로 변환 */
export const approvalSearchFetcher = async <T extends { data?: unknown[] }>(
  url: string,
): Promise<T> => {
  const res = await getNestedMetaDataFetcher<T>(url);
  return { ...res, data: await toLegacyApprovalList(res.data) };
};

/** 결재 목록 검색 fetcher(data 배열만) — 무한 스크롤 목록용 */
export const approvalSearchListFetcher = async <T>(url: string) =>
  toLegacyApprovalList(await getNestedDataFetcher<T[]>(url));

export const useSearchApproval = (
  obj: TotalApprovalParams,
  config?: SWRConfiguration,
  shouldFetch = true,
) =>
  useSWR<ApprovalList>(
    shouldFetch ? buildApprovalSearchUrl(obj) : null,
    approvalSearchFetcher,
    config,
  );

export const useGetFilterRecommend = () =>
  useSWR<RecommendFilterResponse>(
    APPROVAL_RECOMMEND_FILTER_URL,
    getNestedDataFetcher,
  );

export const useGetCombinedDepartment = (code: string) => {
  return useSWR<string[]>(
    code ? `${COMBINED_DEPARTMENT_URL}/${code}` : null,
    getNestedDataFetcher,
  );
};

// --- 검색 키워드 ---

export const deleteApprovalKeywordUsingBody = (keyword: string) =>
  deleteDataFetcher(`${APPROVAL_RECENT_SEARCH_KEYWORD}`, { keyword });

export const useSaveApprovalSearch = (keyword?: string) =>
  keyword ? postDataFetcher(APPROVAL_RECENT_SEARCH_KEYWORD, { keyword }) : null;

// --- 북마크 ---

export const useBookMark = (documentId: string) =>
  fetchApi<void>({
    method: 'POST',
    url: getApprovalBookmarkUrl(documentId),
  });

// --- 즐겨찾기 필터 ---

export const useSetApprovalFavoriteFilter = (obj: FavoriteFilter) =>
  postDataFetcher(APPROVAL_FAVORITE_FILTER, obj);

export const useGetApprovalFavoriteFilter = () =>
  useSWR<FavoriteApprovalFilter>(
    `${APPROVAL_FAVORITE_FILTER}/me?listSize=50`,
    getNestedMetaDataFetcher,
  );

export const useDeleteApprovalFavoriteFilter = (id: string) =>
  deleteDataFetcher(`${APPROVAL_FAVORITE_FILTER}/${id}`);

export const useGetFavoriteFilter = (id?: string) =>
  useSWR<FavoriteFilterData>(
    id ? `${APPROVAL_FAVORITE_FILTER}/${id}` : null,
    getNestedDataFetcher,
  );

export const useModifyFavoriteFilter = ({
  id,
  name,
}: {
  id: string;
  name: string;
}) => putDataFetcher(`${APPROVAL_FAVORITE_FILTER}/${id}/name`, { name: name });

// --- 레거시 (legacyApiHooks.ts에서 통합) ---

export type RecentlyViewedApproval = {
  docId: string;
  subject: string;
  author: {
    name: string;
    positionName: string;
  };
  authoredAt: string;
  viewedAt: string;
};

export const useNewApprovalConfirmMe = (
  passportId: string,
  listSize?: number,
) =>
  useSWR<LegacyApproval>(
    passportId
      ? `${APPROVAL_SEARCH_URL}?currentApproverPassportIds=${passportId}${
          listSize ? '&listSize=' + listSize : ''
        }`
      : null,
    approvalSearchFetcher,
  );

export const useNewApprovalUnreadReferee = (passportId: string) =>
  useSWR<LegacyApproval>(
    passportId
      ? `${APPROVAL_SEARCH_URL}?referrerPassportIds=${passportId}&unreadPassportIds=${passportId}`
      : null,
    approvalSearchFetcher,
  );

export const useNewMyApproval = (passportId: string) =>
  useSWR<LegacyApproval>(
    passportId
      ? `${APPROVAL_SEARCH_URL}?authorPassportIds=${passportId}&statuses=P`
      : null,
    approvalSearchFetcher,
  );

export const useApprovalRecentlyViewedList = () => {
  return useSWR<RecentlyViewedApproval[]>(
    '/api/v1/history/me/approval/viewed/recently',
    getNestedDataFetcher,
  );
};

export const deleteApprovalRecentlyViewed = (docId: string) => {
  return deleteDataFetcher(
    `/api/v1/history/me/approval/viewed/recently/data/${docId}`,
  );
};
